import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Country, State, City } from "country-state-city";
import { useAuth } from "../context/AuthContext";
import { dbService } from "../services/dbService";
import homeBg from "../assets/images/home.jpeg";

export default function Register() {
  const navigate = useNavigate();
  const { t } = useTranslation("global");
  const { signup, verifyEmail } = useAuth();

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [states2, setStates2] = useState([]);
  const [cities2, setCities2] = useState([]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    mobile: "",
    address: "",
    nationality: "",
    tourismDevelopment: "",
    membership: "",
    flyxomMembership: "",
    coowner: "",
    purchasedate: "",
    selectedCountry: "",
    selectedState: "",
    selectedCity: "",
    selectedCountry2: "",
    selectedState2: "",
    selectedCity2: "",
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  useEffect(() => {
    if (formData.selectedCountry) {
      setStates(State.getStatesOfCountry(formData.selectedCountry));
      setCities([]);
    }
  }, [formData.selectedCountry]);

  useEffect(() => {
    if (formData.selectedState) {
      setCities(
        City.getCitiesOfState(formData.selectedCountry, formData.selectedState)
      );
    }
  }, [formData.selectedCountry, formData.selectedState]);

  useEffect(() => {
    if (formData.selectedCountry2) {
      setStates2(State.getStatesOfCountry(formData.selectedCountry2));
      setCities2([]);
    }
  }, [formData.selectedCountry2]);

  useEffect(() => {
    if (formData.selectedState2) {
      setCities2(
        City.getCitiesOfState(
          formData.selectedCountry2,
          formData.selectedState2
        )
      );
    }
  }, [formData.selectedCountry2, formData.selectedState2]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSelectChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => {
      const updates = { [id]: value };
      if (id === "selectedCountry") {
        updates.selectedState = "";
        updates.selectedCity = "";
      } else if (id === "selectedCountry2") {
        updates.selectedState2 = "";
        updates.selectedCity2 = "";
      }
      return { ...prev, ...updates };
    });
  };

  const isValidPassword = (password) => {
    const policy = {
      minLength: 8,
      requireLowercase: true,
      requireUppercase: true,
      requireNumbers: true,
      requireSpecialCharacters: true,
    };

    if (password.length < policy.minLength) return false;
    if (policy.requireLowercase && !/[a-z]/.test(password)) return false;
    if (policy.requireUppercase && !/[A-Z]/.test(password)) return false;
    if (policy.requireNumbers && !/\d/.test(password)) return false;
    if (
      policy.requireSpecialCharacters &&
      !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    )
      return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!isValidPassword(formData.password)) {
      setError(t("Alert6.text"));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t("PasswordMismatch.text"));
      return;
    }

    setLoading(true);

    try {
      const user = await signup(formData.email, formData.password);

      // Prepare user data for DB
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        address: formData.address,
        city: formData.selectedCity,
        state: formData.selectedState,
        country: formData.selectedCountry,
        nationality: formData.nationality,
        phoneNumber: formData.phone,
        mobile: formData.mobile,
        tourism_development: formData.tourismDevelopment,
        membership: formData.membership,
        flyxom_membership: formData.flyxomMembership,
        coowner: formData.coowner,
        date: formData.purchasedate,
        membercountry: formData.selectedCountry2,
        memberstate: formData.selectedState2,
        membercity: formData.selectedCity2,
      };

      await dbService.writeUserData(user.uid, userData);
      await verifyEmail(user);

      alert(t("Welcome.text"));
      navigate("/survey", { replace: true });
    } catch (error) {
      const errorCode = error.code;
      const errorMessage = error.message;
      console.log(errorCode, errorMessage);
      setError(t("Alert5.text") + ": " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex items-center justify-center w-full relative min-h-screen bg-center bg-no-repeat bg-cover py-10"
      style={{ backgroundImage: `url(${homeBg})` }}
    >
      <div className="overflow-y-auto max-h-screen bg-gray-800/50 bg-blend-multiply shadow-md rounded-lg px-8 py-8 backdrop-filter backdrop-blur-md w-[40rem]">
        <h1 className="text-2xl font-bold text-center mb-4 dark:text-gray-200">
          {t("Join.text")}
        </h1>
        <p className="border-b-2 border-gray-500 mb-4"></p>

        {error && (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
            role="alert"
          >
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4 pt-4">
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("First_Name.text")}
            </label>
            <input
              id="firstName"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              placeholder="John"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Last_Name.text")}
            </label>
            <input
              id="lastName"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              placeholder="Doe"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Email.text")}
            </label>
            <input
              type="email"
              id="email"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              placeholder="flyxom@email.com"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Password.text")}
            </label>
            <input
              type="password"
              id="password"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              placeholder={t("Password_Registry.text")}
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Confirm_Password.text")}
            </label>
            <input
              type="password"
              id="confirmPassword"
              className={`w-full py-3 px-3 rounded-lg bg-gray-700/80 border text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none ${
                formData.password !== formData.confirmPassword &&
                formData.confirmPassword !== ""
                  ? "border-red-500"
                  : "border-gray-600"
              }`}
              placeholder={t("Confirm_Password.text")}
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
            {formData.password !== formData.confirmPassword &&
              formData.confirmPassword !== "" && (
                <p className="text-red-500 text-sm mt-1">
                  {t("PasswordMismatch.text")}
                </p>
              )}
          </div>

          <div className="mb-4">
            <label
              htmlFor="address"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Address.text")}
            </label>
            <input
              id="address"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              placeholder="123 Main St"
              value={formData.address}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="selectedCountry"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Country.text")}
            </label>
            <select
              id="selectedCountry"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              value={formData.selectedCountry}
              onChange={handleSelectChange}
              required
            >
              <option value="">{t("Select_Country.text")}</option>
              {countries.map((country) => (
                <option key={country.isoCode} value={country.isoCode}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label
              htmlFor="selectedState"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("State.text")}
            </label>
            <select
              id="selectedState"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              value={formData.selectedState}
              onChange={handleSelectChange}
              required
            >
              <option value="">{t("Select_State.text")}</option>
              {states.map((state) => (
                <option key={state.isoCode} value={state.isoCode}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label
              htmlFor="selectedCity"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("City.text")}
            </label>
            <select
              id="selectedCity"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              value={formData.selectedCity}
              onChange={handleSelectChange}
              required
            >
              <option value="">{t("Select_City.text")}</option>
              {cities.map((city) => (
                <option key={city.name} value={city.name}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label
              htmlFor="nationality"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Nationality.text")}
            </label>
            <input
              id="nationality"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              placeholder="Mexican"
              value={formData.nationality}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Phone.text")}
            </label>
            <input
              id="phone"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              placeholder="123-456-7890"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="mobile"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Mobile.text")}
            </label>
            <input
              id="mobile"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              placeholder="123-456-7890"
              value={formData.mobile}
              onChange={handleChange}
              required
            />
          </div>
          <p className="pt-4 pb-4 border-b-2 border-gray-500 text-center  text-xl font-bold dark:text-gray-200">
            {t("Membership.text")}
          </p>

          <div className="mb-4 pt-4">
            <label
              htmlFor="tourismDevelopment"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Tourism Development.text")}
            </label>
            <input
              id="tourismDevelopment"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              placeholder="Vidanta"
              value={formData.tourismDevelopment}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="membership"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Membership.text")}
            </label>
            <input
              id="membership"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              placeholder="123-456-7890"
              value={formData.membership}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="flyxomMembership"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Flyxom_Membership.text")}
            </label>
            <input
              id="flyxomMembership"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              placeholder="123-456-7890"
              value={formData.flyxomMembership}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="coowner"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Coowner Name.text")}
            </label>
            <input
              id="coowner"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              placeholder="Karen N"
              value={formData.coowner}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="purchasedate"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Purchase Date.text")}
            </label>
            <input
              type="date"
              id="purchasedate"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none [color-scheme:dark] cursor-pointer"
              value={formData.purchasedate}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="selectedCountry2"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("Country.text")}
            </label>
            <select
              id="selectedCountry2"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              value={formData.selectedCountry2}
              onChange={handleSelectChange}
              required
            >
              <option value="">{t("Select_Country.text")}</option>
              {countries.map((country) => (
                <option key={country.isoCode} value={country.isoCode}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label
              htmlFor="selectedState2"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("State.text")}
            </label>
            <select
              id="selectedState2"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              value={formData.selectedState2}
              onChange={handleSelectChange}
              required
            >
              <option value="">{t("Select_State.text")}</option>
              {states2.map((state) => (
                <option key={state.isoCode} value={state.isoCode}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label
              htmlFor="selectedCity2"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              {t("City.text")}
            </label>
            <select
              id="selectedCity2"
              className="w-full py-3 px-3 rounded-lg bg-gray-700/80 border border-gray-600 text-gray-400 font-semibold focus:border-[#fcab21] focus:outline-none"
              value={formData.selectedCity2}
              onChange={handleSelectChange}
              required
            >
              <option value="">{t("Select_City.text")}</option>
              {cities2.map((city) => (
                <option key={city.name} value={city.name}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-xl text-sm md:text-base text-white font-semibold bg-[#fcab21] hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#fcab21] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {loading ? "Loading..." : t("Create_Account.text")}
          </button>
          <p className="pb-4 pt-4 border-b-2 border-gray-500"></p>
        </form>
        <div className="flex justify-center items-center mt-6">
          <span className="text-sm text-gray-300">
            {t("Back_Login.text")}{" "}
            <NavLink
              to={"/login"}
              className="text-[#fcab21] hover:text-yellow-600 font-semibold focus:outline-none transition-colors duration-200"
            >
              {t("Login_Link.text")}
            </NavLink>
          </span>
        </div>
      </div>
    </div>
  );
}
